import React from 'react';
import QueryString from 'qs';
import dayjs from 'dayjs';
import { useQuery } from '@tanstack/react-query';
import { Box, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import CommonText from '../../../form-components/CommonText';
import { getStopHistory } from '../../../api/member-course-history';
import { formatDate } from '../../../utils/methods';
import {
  SectionHeader,
  SubsectionLabel,
  MoreLink,
  DataTableContainer,
  DataTableHeadCell,
  DataTableBodyCell,
  StatCardGrid,
  StatCard,
  StatLabel,
  StatValue,
} from './UserGeneralTabStyled';

// 라벨 + 값 한 쌍을 카드 형태로.
const Stat = ({ label, value }) => (
  <StatCard>
    <StatLabel>{label}</StatLabel>
    <StatValue>{value ?? '-'}</StatValue>
  </StatCard>
);

// "내용이 없습니다" 표시 행 — 데이터 없을 때 테이블 본문 자리.
const EmptyRow = ({ colSpan }) => (
  <TableBody>
    <TableRow>
      <TableCell colSpan={colSpan} align="center" sx={{ py: '32px' }}>
        <CommonText variant="caption" sx={{ color: '#A6A6A6' }}>
          내용이 없습니다.
        </CommonText>
      </TableCell>
    </TableRow>
  </TableBody>
);

// 테이블 헤더 행 — 라벨과 "자세히 보기" 링크가 함께 있는 줄.
// 라벨/링크의 세로 위치·글자 크기를 맞추기 위해 baseline 정렬 + 라벨 자체 marginBottom 제거,
// 표와의 간격은 행(Box)의 mb 로 대신 준다.
const TableSectionHeader = ({ t, label, onMore }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', mt: 3, mb: 1 }}>
    <SubsectionLabel variant="caption" sx={{ mb: 0 }}>
      {label}
    </SubsectionLabel>
    <MoreLink onClick={onMore}>{t('자세히 보기')}</MoreLink>
  </Box>
);

// "이용 정보" 블록 — 통계 3개 + 최근 수업 예약 풀폭 테이블.
const UsageInfoSection = ({ t, userInfo, onMore }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <TableSectionHeader t={t} label={t('최근 수업 예약 내역')} onMore={onMore} />
    <DataTableContainer>
      <Table sx={{ width: '100%', textAlign: 'center' }} size="small" aria-label="recent reservations">
        <TableHead sx={{ background: '#FAFBFC' }}>
          <TableRow>
            <DataTableHeadCell align="center">{t('수업 일시')}</DataTableHeadCell>
            <DataTableHeadCell align="center">{t('예약 일시')}</DataTableHeadCell>
            <DataTableHeadCell align="center">{t('클래스명')}</DataTableHeadCell>
            <DataTableHeadCell align="center" sx={{ borderRight: 'none' }}>
              {t('Management')}
            </DataTableHeadCell>
          </TableRow>
        </TableHead>
        {userInfo?.reservations ? (
          <TableBody>
            {userInfo.reservations.map((lesson, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                }}>
                <DataTableBodyCell align="center" component="th" scope="row">
                  {formatDate(lesson.classDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                </DataTableBodyCell>
                <DataTableBodyCell align="center">
                  {formatDate(lesson.reservedDateTime, 'YYYY.MM.DD (dd) A hh:mm')}
                </DataTableBodyCell>
                <DataTableBodyCell align="center">{lesson.className}</DataTableBodyCell>
                <DataTableBodyCell align="center" sx={{ borderRight: 'none' }}>
                  {lesson.representativeTrainer}
                </DataTableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <EmptyRow colSpan={4} />
        )}
      </Table>
    </DataTableContainer>
  </Box>
);

// "센터 이용 정보" 블록 — 사용 중인 수강권 풀폭 테이블.
const CenterUsageSection = ({ t, userInfo, onMore }) => (
  <Box sx={{ flex: 1, minWidth: 0 }}>
    <TableSectionHeader t={t} label={t('사용 중인 수강권')} onMore={onMore} />
    <DataTableContainer>
      <Table sx={{ width: '100%' }} size="small" aria-label="active tickets">
        <TableHead sx={{ background: '#FAFBFC' }}>
          <TableRow>
            <DataTableHeadCell align="center" sx={{ width: '15%' }}>
              {t('구분')}
            </DataTableHeadCell>
            <DataTableHeadCell align="center" sx={{ width: '42%' }}>
              {t('수강권 명')}
            </DataTableHeadCell>
            <DataTableHeadCell align="center">{t('만료일')}</DataTableHeadCell>
            <DataTableHeadCell align="center" sx={{ borderRight: 'none' }}>
              {t('잔여횟수')}
            </DataTableHeadCell>
          </TableRow>
        </TableHead>
        {userInfo?.activeTickets ? (
          <TableBody>
            {userInfo.activeTickets.map((ticket, index) => (
              <TableRow
                key={index}
                sx={{
                  '&:last-child td, &:last-child th': { borderBottom: 0 },
                }}>
                <DataTableBodyCell align="center" component="th" scope="row">
                  {t(ticket.courseClassType)}
                </DataTableBodyCell>
                <DataTableBodyCell align="center">{ticket.ticketName}</DataTableBodyCell>
                <DataTableBodyCell align="center">{ticket.ticketExpireDate}</DataTableBodyCell>
                <DataTableBodyCell align="center" sx={{ borderRight: 'none' }}>
                  {ticket.remainingCount}
                </DataTableBodyCell>
              </TableRow>
            ))}
          </TableBody>
        ) : (
          <EmptyRow colSpan={4} />
        )}
      </Table>
    </DataTableContainer>
  </Box>
);

// "최근 중지 내역" 블록 — 중지 이력 API에서 중지(STOPPED)건만 최근 5건.
const RECENT_STOP_LIMIT = 5;

// 잔여 중지 횟수는 이력 시점의 스냅샷이다.
//   stopUnlimited=true  → 중지 제한 없음
//   stopUnlimited=false → remainingStopCount 회
//   stopUnlimited=null  → 스냅샷 도입 전 이력 = 알 수 없음('-'). "무제한"과 혼동되지 않게 구분한다.
const formatRemainingStopCount = (t, stop) => {
  if (stop.stopUnlimited === true) return t('무제한');
  // 강제중지로 잔여가 음수일 수 있어 표시만 0으로 하한(도메인 값은 음수 유지).
  if (stop.stopUnlimited === false && stop.remainingStopCount != null) return `${Math.max(0, stop.remainingStopCount)}회`;
  return '-';
};

const RecentStopsSection = ({ t, memberId, branchId, token, onMore }) => {
  const { data } = useQuery({
    queryKey: ['memberStopHistory', memberId, branchId],
    queryFn: () =>
      getStopHistory(QueryString.stringify({ branchId, memberId, page: 1, limit: 20 }), token),
    enabled: !!memberId && !!branchId && !!token,
  });

  // 중지해제(STOP_CANCELED)는 제외하고 실제 중지건만 노출.
  const stops = (data?.content || []).filter(row => row.actionType === 'STOPPED').slice(0, RECENT_STOP_LIMIT);

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TableSectionHeader t={t} label={t('최근 중지 내역')} onMore={onMore} />
      <DataTableContainer>
        <Table sx={{ width: '100%' }} size="small" aria-label="recent stops">
          <TableHead sx={{ background: '#FAFBFC' }}>
            <TableRow>
              <DataTableHeadCell align="center">{t('중지 기간')}</DataTableHeadCell>
              <DataTableHeadCell align="center" sx={{ width: '35%' }}>
                {t('상품명')}
              </DataTableHeadCell>
              <DataTableHeadCell align="center">{t('잔여 중지 횟수')}</DataTableHeadCell>
              <DataTableHeadCell align="center" sx={{ borderRight: 'none' }}>
                {t('Management')}
              </DataTableHeadCell>
            </TableRow>
          </TableHead>
          {stops.length > 0 ? (
            <TableBody>
              {stops.map(stop => (
                <TableRow key={`${stop.category}-${stop.id}`} sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                  <DataTableBodyCell align="center">
                    {formatDate(stop.stoppedDate, 'YYYY-MM-DD')} ~ {formatDate(stop.stoppedEndDate, 'YYYY-MM-DD')}
                  </DataTableBodyCell>
                  <DataTableBodyCell align="center">{stop.productName}</DataTableBodyCell>
                  <DataTableBodyCell align="center">{formatRemainingStopCount(t, stop)}</DataTableBodyCell>
                  <DataTableBodyCell align="center" sx={{ borderRight: 'none' }}>
                    {stop.regName}
                  </DataTableBodyCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <EmptyRow colSpan={4} />
          )}
        </Table>
      </DataTableContainer>
    </Box>
  );
};

// "만료예정 회원권" 블록 — 사용 중인 수강권 중 만료일이 임박(기준일 이내)한 것.
// NOTE: 기준일은 우선 클라이언트 기본값(EXPIRING_SOON_DAYS). 추후 지점 "만료임박 기준" 설정
//       (StandardModal / 회원목록 EXPIRING_SOON)을 재사용하도록 개선 권장.
const EXPIRING_SOON_DAYS = 30;

const ExpiringTicketsSection = ({ t, userInfo, onMore }) => {
  const today = dayjs().startOf('day');
  const limit = today.add(EXPIRING_SOON_DAYS, 'day');

  // 만료일이 오늘 ~ 기준일(EXPIRING_SOON_DAYS) 사이인 활성 수강권만, 만료 임박순.
  // dayjs로 파싱해 날짜 포맷 변형(타임스탬프 포함 등)에도 안전하게 비교/정렬한다.
  const expiring = (userInfo?.activeTickets || [])
    .filter(ticket => {
      const exp = dayjs(ticket.ticketExpireDate);
      return exp.isValid() && !exp.isBefore(today) && !exp.isAfter(limit);
    })
    .sort((a, b) => dayjs(a.ticketExpireDate).valueOf() - dayjs(b.ticketExpireDate).valueOf());

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <TableSectionHeader t={t} label={t('만료예정 회원권')} onMore={onMore} />
      <DataTableContainer>
        <Table sx={{ width: '100%' }} size="small" aria-label="expiring tickets">
          <TableHead sx={{ background: '#FAFBFC' }}>
            <TableRow>
              <DataTableHeadCell align="center" sx={{ width: '15%' }}>
                {t('구분')}
              </DataTableHeadCell>
              <DataTableHeadCell align="center" sx={{ width: '42%' }}>
                {t('수강권 명')}
              </DataTableHeadCell>
              <DataTableHeadCell align="center">{t('만료일')}</DataTableHeadCell>
              <DataTableHeadCell align="center" sx={{ borderRight: 'none' }}>
                {t('잔여횟수')}
              </DataTableHeadCell>
            </TableRow>
          </TableHead>
          {expiring.length > 0 ? (
            <TableBody>
              {expiring.map((ticket, index) => (
                <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { borderBottom: 0 } }}>
                  <DataTableBodyCell align="center" component="th" scope="row">
                    {t(ticket.courseClassType)}
                  </DataTableBodyCell>
                  <DataTableBodyCell align="center">{ticket.ticketName}</DataTableBodyCell>
                  <DataTableBodyCell align="center">{ticket.ticketExpireDate}</DataTableBodyCell>
                  <DataTableBodyCell align="center" sx={{ borderRight: 'none' }}>
                    {ticket.remainingCount}
                  </DataTableBodyCell>
                </TableRow>
              ))}
            </TableBody>
          ) : (
            <EmptyRow colSpan={4} />
          )}
        </Table>
      </DataTableContainer>
    </Box>
  );
};

// 회원 활동 한눈에 보기 — 두 컬럼 테이블 위에 띄우는 요약 통계.
const ActivitySummary = ({ t, userInfo }) => (
  <StatCardGrid>
    <Stat label={t('누적 방문 수')} value={userInfo?.totalAttendanceCount} />
    <Stat label={t('첫 결제일')} value={userInfo?.firstPaymentDate} />
    <Stat label={t('최근 방문일')} value={userInfo?.lastAttendanceDate} />
  </StatCardGrid>
);

const UserUsageTables = ({
  t,
  userInfo,
  memberId,
  branchId,
  token,
  onMoreReservations,
  onMoreTickets,
  onMoreStops,
}) => (
  <Box
    sx={{ mt: 3, mb: 3, py: 1, px: 2, backgroundColor: '#FAFBFC', border: '1px solid #ebebeb', borderRadius: '8px' }}>
    <SectionHeader>{t('이용 정보')}</SectionHeader>
    <ActivitySummary t={t} userInfo={userInfo} />
    <Box sx={{ display: 'flex', gap: 3 }}>
      <UsageInfoSection t={t} userInfo={userInfo} onMore={onMoreReservations} />
      <CenterUsageSection t={t} userInfo={userInfo} onMore={onMoreTickets} />
    </Box>
    <Box sx={{ display: 'flex', gap: 3, pb: 2 }}>
      <RecentStopsSection t={t} memberId={memberId} branchId={branchId} token={token} onMore={onMoreStops} />
      <ExpiringTicketsSection t={t} userInfo={userInfo} onMore={onMoreTickets} />
    </Box>
  </Box>
);

export default UserUsageTables;
